
import { NgModule, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { DOMModule } from './dom.module';
import { ClipboardService } from './services/clipboard.service';
import { ToasterModule } from './toaster-module/toaster.module';
import { DetailScreensModule } from './components/po-treeview/detail-screens-module/detail-screens.module';
import { UiHelperService } from './services/shared/helpers/ui-helper.service';
import { MeatBallMenuModule } from './services/shared/meatball-menu/meatball-menu.module';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { PoTreeviewComponent } from './components/po-treeview/po-treeview.component';
import { ProductServiceComponent } from './components/po-treeview/popup-component/templates/product-service/product-service.component';
import { PopUpComponent } from './components/po-treeview/popup-component/popup.component';
import { PONodeComponent } from './components/po-treeview/abstract-classes/po-node/ponode.component';
import { ClusterNodeComponent } from './components/po-treeview/cluster-node/cluster-node.component';
import { CategoryNodeComponent } from './components/po-treeview/category-node/category-node.component';
import { SubCategoryNodeComponent } from './components/po-treeview/sub-catergory-node/sub-category-node.component';
import { ProductNodeComponent } from './components/po-treeview/product-node/product-node.component';
import { ArrangementNodeComponent } from './components/po-treeview/arrangement-node/arrangement-node.component';
import { MoreNodeComponent } from './components/po-treeview/more-node/more-node.component';
import { ExpiredNodeComponent } from './components/po-treeview/expired-node/expired-node.component';
import { SubArrangementNodeComponent } from './components/po-treeview/sub-arrangement-node/sub-arrangement-node.component';
import { ClipboardDirective } from './components/clipboard.directive';
import { ArrangementLevelComponent } from './components/po-treeview/abstract-classes/arrangement-level/arrangement-level.component';
import { CivMainComponent } from './components/civ-main/civ-main.component';
import { CollateralObjectsComponent } from './components/collateral-objects/collateral-objects.component';
import { CustomerContributionComponent } from './components/customer-contribution/customer-contribution.component';
import { ExpandAllButtonComponent } from './components/po-treeview/abstract-classes/expand-all-button/expand-all-button.component';
import { ExpandAllDirective } from './components/expand-all.directive';
import { HoverDirective } from './components/po-treeview/hover.directive';

import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalBroadcastService,
  MsalService,
  MsalGuard,
  MsalModule,
  MsalInterceptor,
} from '@azure/msal-angular';

import {
  IPublicClientApplication,
  PublicClientApplication,
  BrowserCacheLocation,
  LogLevel,
  InteractionType,
} from '@azure/msal-browser';

import { SfcModule } from '@sfc/angular';
import { environment } from '../environments/environment';
import { TokenService } from './services/auth/token.service';

registerLocaleData(localeNl, 'nl');

const isIE = window.navigator.userAgent.indexOf('MSIE') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: 'https://login.microsoftonline.com/6e93a626-8aca-4dc1-9191-ce291b4b75a1/v2.0',
      redirectUri: window.location.origin + '/auth_popup.html',
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      allowNativeBroker: false,
      asyncPopups: true,
      allowRedirectInIframe: false,
      loggerOptions: {
        loggerCallback: (lvl, msg) => console.log(msg),
        logLevel: LogLevel.Info
      },
      windowHashTimeout: 13000,
      iframeHashTimeout: 6000,
    },
  });
}

export function initializeMsal(msalInstance: IPublicClientApplication) {
  return () => msalInstance.initialize();
}

export function initializeTokenService(tokenService: TokenService): () => void {
  return () => tokenService.startTokenRefreshScheduler();
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, (string | null)[] | null>();

  protectedResourceMap.set(environment.civServicesBaseURL + '*', environment.apiScopes);

  protectedResourceMap.set(environment.civViewBaseURL + '*', environment.apiScopes);

  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

@NgModule({
  declarations: [
    ExpandAllDirective,
    HoverDirective,
    AppComponent,
    PoTreeviewComponent,
    PONodeComponent,
    ClusterNodeComponent,
    CategoryNodeComponent,
    SubCategoryNodeComponent,
    ProductNodeComponent,
    ArrangementNodeComponent,
    MoreNodeComponent,
    ExpiredNodeComponent,
    SubArrangementNodeComponent,
    ClipboardDirective,
    ArrangementLevelComponent,
    CivMainComponent,
    CollateralObjectsComponent,
    CustomerContributionComponent,
    ExpandAllButtonComponent,
    ProductServiceComponent,
    PopUpComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    AppRoutingModule,

    MatProgressBarModule,
    MatTooltipModule,
    MatDividerModule,
    MatTableModule,
    MatExpansionModule,
    MatIconModule,
    MatMenuModule,

    DOMModule,
    ToasterModule,
    DetailScreensModule,
    MeatBallMenuModule,
    MsalModule,
    SfcModule,
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: initializeMsal, deps: [MSAL_INSTANCE], multi: true },
    { provide: APP_INITIALIZER, useFactory: initializeTokenService, deps: [TokenService], multi: true },

    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },

    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    { provide: LOCALE_ID, useValue: 'nl-NL' },
    ClipboardService,
    UiHelperService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class AppModule {}
